/**
 * Network Probe Service
 *
 * Centralizes low-level connectivity probes that MUST bypass the axios-based
 * service layer (and its auth/tenant interceptors).
 *
 * WHY raw fetch?
 * - WebSocket preflight: must probe the actual WS path *before* auth tokens are
 *   available and without triggering the 401-refresh interceptor.
 * - Health recovery ping: used to detect backend revival after a 5xx outage;
 *   using the axios client would itself be affected by the circuit-breaker state.
 *
 * Only this service is allowed to call the global `fetch`. All other network
 * calls must use `businessApi` / `workformsApi` (frontend) or `ApiService` (mobile).
 */

import { logger } from '@/utils/logger';

/** The WebSocket inbox path — also used for HTTP pre-flight probing. */
export const AI_INBOX_WS_PATH = '/ws/ai/inbox/';

/**
 * HTTP status codes that confirm the WS path is routed to ASGI (Daphne).
 *   200 — ASGI health probe responds
 *   426 — Upgrade Required (endpoint exists but requires WS upgrade)
 * Any other status means the path is NOT routed to ASGI.
 */
const WS_PREFLIGHT_ALLOWED_STATUSES = new Set([200, 426]);

/**
 * Pre-flight check: verify the WebSocket path is routed to ASGI before
 * attempting a WebSocket connection. Probes the actual WS path with HTTP GET.
 *
 * Returns true only if the response looks like a real ASGI/Django endpoint
 * (not the SPA fallback serving index.html for unknown routes).
 *
 * Intentionally bypasses auth interceptors — this is a connectivity probe,
 * not an authenticated API call.
 */
export async function checkWSEndpointReachable(
  wsPath: string = AI_INBOX_WS_PATH
): Promise<boolean> {
  try {
    const response = await fetch(wsPath, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
      redirect: 'manual',
      signal: AbortSignal.timeout(5000),
    });

    if (response.redirected) {
      return false;
    }

    if (!WS_PREFLIGHT_ALLOWED_STATUSES.has(response.status)) {
      return false;
    }

    // Guard against SPA fallback: nginx may serve index.html (200 text/html)
    // for unknown routes. A real ASGI endpoint returns 426 or JSON, not HTML.
    const contentType = response.headers.get('content-type') ?? '';
    if (response.status === 200 && contentType.includes('text/html')) {
      logger.debug('[networkProbeService] WS preflight got HTML — likely SPA fallback, not ASGI');
      return false;
    }

    // A 200 JSON response is an HTTP-only healthcheck route; it cannot handle
    // the WebSocket protocol upgrade.
    if (response.status === 200 && contentType.includes('application/json')) {
      logger.debug(
        '[networkProbeService] WS preflight got JSON 200 — HTTP-only endpoint, not WS-capable'
      );
      return false;
    }

    return true;
  } catch (err) {
    logger.debug('[networkProbeService] WS endpoint preflight failed — ASGI not available:', err);
    return false;
  }
}

/**
 * Lightweight health ping.
 *
 * Used by the server-recovery hook to detect backend revival after a 5xx
 * outage. Must use raw fetch so it is unaffected by the axios circuit-breaker
 * state that triggered the recovery check in the first place.
 *
 * Returns true if the endpoint responds with a 2xx status.
 */
export async function pingHealthEndpoint(url: string): Promise<boolean> {
  try {
    const res = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      signal: AbortSignal.timeout(5000),
    });
    return res.ok;
  } catch {
    return false;
  }
}
